<template>
    <div id="optionCalculator">
        <div class="border-t-2 border-blue-600 mt-2 md:mt-4">
            <div class="tan-control-box text-center pb-2">
                <div class="mb-2 mx-auto font-bold text-lg">Select Futures:</div>
                <option-selector-2 @underlying="setUnderlying" @option="setOptionSymbol" :initial-symbol="initialSymbol" class="selector-wrapper" v-model="state.symbol" :groups="groups"></option-selector-2>
            </div>
        </div>

        <div v-if="state.loading">
            <spinner></spinner>
        </div>
        <div v-else class="flex flex-col md:flex-row items-stretch mt-8">
            <div class="flex-1 bg-tan-200 border border-t-0 md:mr-2 mb-8 md:mb-0 ">
                <div v-if="state.symbol && state.baseCode" class="border-t-2 border-blue-600 p-8">
                    <div class="font-bold text-lg">Probability Calculator</div>
                    <div class="flex flex-wrap w-full mt-3">
                        <div class="w-full md:w-1/2 md:pr-2 mb-2">
                            <label for="probability-price">Futures Price</label>
                            <price-input :base-code="state.baseCode" class="form-control-sm form-input-modal" type="text" v-model="state.probabilityForm.price" :default-value="state.probabilityForm.price" id="probability-price" :symbol="state.symbol"></price-input>
                        </div>

                        <div class="w-full md:w-1/2 md:pl-2 mb-2">
                            <label for="probability-days"># Days to Expiration</label>
                            <input class="form-control form-control-sm form-input-modal" id="probability-days" v-model="state.probabilityForm.daysToExpiration" type="number" name="probability-days">
                        </div>
                    </div>
                    <div class="flex flex-wrap w-full">
                        <div class="w-full md:w-1/2 md:pr-2 mb-2">
                            <label for="probability-implied">Implied Volatility</label>
                            <input class="form-control form-control-sm form-input-modal" type="text" v-model="state.probabilityForm.impliedVol" id="probability-implied">
                        </div>

                        <div class="w-full md:w-1/2 md:pl-2 mb-2">
                            <label for="probability-strike">Strike Price</label>
                            <price-input :base-code="state.baseCode" class="form-control-sm form-input-modal" type="text" v-model="state.probabilityForm.strike" :default-value="state.probabilityForm.strike" id="probability-strike"></price-input>
                        </div>
                    </div>
                    <div class="flex justify-center mt-4">
                        <div class="btn btn-yellow inline-flex" @click="calcProb">Calculate Probability</div>
                    </div>
                </div>
                <div class="border-t-5 border-blue-600 p-8" v-if="state.probabilityResult">
                    <div class="font-bold text-md text-blue-600">PROBABILITY RESULTS</div>
                    <p class="paragraph">Probability of market finishing:</p>
                    <table class="table">
                        <tbody>
                            <tr>
                                <th>Above Strike Price</th>
                                <td>{{ state.probabilityResult.above }}%</td>
                            </tr>
                            <tr>
                                <th>Below Strike Price</th>
                                <td>{{ state.probabilityResult.below }}%</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div class="flex-1 bg-tan-200 border border-t-0 md:ml-2">
                <div v-if="state.symbol && state.baseCode" class="border-t-2 border-blue-600 p-8">
                    <div class="font-bold text-lg">Selling Calculator</div>
                    <div class="flex flex-wrap w-full mt-3">
                        <div class="w-full md:w-1/2 md:pr-2 mb-2">
                            <label for="sell-price">Futures Price</label>
                            <price-input :base-code="state.baseCode" class="form-control form-control-sm form-input-modal" :default-value="state.sellForm.price" v-model="state.sellForm.price" id="sell-price"></price-input>
                        </div>

                        <div class="w-full md:w-1/2 md:pl-2 mb-2">
                            <label for="sell-days"># Days to Expiration</label>
                            <input class="form-control form-control-sm form-input-modal" id="sell-days" v-model="state.sellForm.daysToExpiration" type="number" name="sell-days">
                        </div>
                    </div>
                    <div class="flex flex-wrap w-full">
                        <div class="w-full md:w-1/2 md:pr-2 mb-2">
                            <label for="sell-implied">Implied Volatility</label>
                            <input class="form-control form-control-sm form-input-modal" type="text" v-model="state.sellForm.impliedVol" id="sell-implied">
                        </div>
                    </div>
                    <div class="flex justify-center mt-4">
                        <div class="btn btn-yellow inline-flex" @click="calcSell">Calculate Sell Price</div>
                    </div>
                </div>
                <div class="border-t-5 border-blue-600 p-8" v-if="state.sellResult">
                    <div class="text-blue-600 font-bold text-md">SELLING RESULTS</div>
                    <p class="paragraph">Implied Volatility is pricing in a {{ sellDiff }}% chance that the market will make a point move {{ state.sellResult.move }} in {{ state.sellForm.daysToExpiration }} days.</p>
                    <table class="table">
                        <thead>
                            <tr>
                                <th>Sell:</th>
                                <th>1 Std. Deviations ({{ state.sellResult.stDev1 }})</th>
                                <th>2 Std. Deviations ({{ state.sellResult.stDev2 }})</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <th>Call Strikes Above</th>
                                <td>{{ state.sellResult.above.stDev1 }}</td>
                                <td>{{ state.sellResult.above.stDev2 }}</td>
                            </tr>
                            <tr>
                                <th>Put Strikes Above</th>
                                <td>{{ state.sellResult.below.stDev1 }}</td>
                                <td>{{ state.sellResult.below.stDev2 }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

        </div>
    </div>
</template>

<script setup>
import { ref, watch, reactive, computed } from 'vue';

// Props
const props = defineProps({
    groups: Object,
    initialSymbol: Object
});

// Reactive state
const state = reactive({
    symbol: null,
    underlying: null,
    option: null,
    baseCode: null,
    sellForm: {
        price: null,
        daysToExpiration: null,
        impliedVol: null
    },
    sellResult: null,
    probabilityForm: {
        price: null,
        daysToExpiration: null,
        impliedVol: null,
        strike: null
    },
    loading: false,
    probabilityResult: null
});

// Computed properties
const sellDiff = computed(() => parseFloat(100 - state.sellForm.impliedVol).toFixed(2));

// Methods
const calcSell = async () => {
    try {
        const route = window.route('options.calculate-sell');
        const response = await axios.post(route, state.sellForm);
        state.sellResult = response.data;
    } catch (error) {
        // Handle error
    }
};

const calcProb = async () => {
    try {
        const route = window.route('options.calculate-probability');
        const response = await axios.post(route, state.probabilityForm);
        state.probabilityResult = response.data;
    } catch (error) {
        // Handle error
    }
};

const setUnderlying = (underlying) => {
    state.underlying = underlying;
};

const setOptionSymbol = (symbol) => {
    state.option = symbol;
};

const getUnderlyingQuotes = async () => {
    try {
        const route = window.route('api.quotes');
        const response = await axios.post(route, { symbol: state.underlying, fields: 'lastPrice,baseCode' });
        const row = response.data.data[0];
        state.sellForm.price = row.raw.lastPrice;
        state.baseCode = row.baseCode;
        state.probabilityForm.price = row.raw.lastPrice;
        return response;
    } catch (error) {
        // Handle error
    }
};

const getStrikePrice = async () => {
    try {
        const route = window.route('api.quotes');
        const response = await axios.post(route, { symbol: state.symbol, fields: 'strikePrice', raw: true });
        state.probabilityForm.strike = response.data.data[0].raw.strikePrice;
    } catch (error) {
        // Handle error
    }
};

const getOptionQuotes = async () => {
    try {
        const route = window.route('api.quotes');
        const response = await axios.post(route, { symbol: state.option, fields: 'daysToExpiration,impliedVolatility', method: 'futuresOptions' });
        const row = response.data.data[0];
        state.sellForm.daysToExpiration = row?.raw.daysToExpiration || '0';
        state.probabilityForm.daysToExpiration = row?.raw.daysToExpiration || '0';
        state.sellForm.impliedVol = row?.raw.impliedVolatility || '0';
        state.probabilityForm.impliedVol = row?.raw.impliedVolatility || '0';
        // Additional logic if necessary
        return response;
    } catch (error) {
        // Handle error
    }
};

const getQuotes = async () => {
    await Promise.all([
        getUnderlyingQuotes(),
        getStrikePrice(),
        getOptionQuotes()
    ]);
};

// Watchers
watch(() => state.symbol, async (newSymbol) => {
    if (newSymbol) {
        state.loading = true;
        try {
            await getQuotes();
        } finally {
            state.loading = false;
        }
        state.probabilityResult = null;
        state.sellResult = null;
    }
});
</script>

<style lang="scss">
#optionCalculator {
    @apply mt-6 mb-8;

    .table {
        thead {

            td,
            th {
                @apply px-3 py-2 text-right;
            }

            td:first-child,
            th:first-child {
                @apply px-3 py-2 text-left;
            }
        }

        thead {
            tr {
                @apply border-t;
            }
        }

        tbody {

            td,
            th {
                @apply px-3 text-right;
            }

            td:first-child,
            th:first-child {
                @apply px-3 py-2 text-left;
            }
        }
    }
}
</style>
